import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Bell, BellRing, ExternalLink, Pencil, Trash2 } from "lucide-react";
import type { PriceAlert } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePriceAlertMutations, usePriceAlerts } from "@/lib/price-alerts";
import { useSettings } from "@/lib/settings";
import { PriceAlertDialog } from "@/components/price-alert-dialog";

export const Route = createFileRoute("/_site/account/price-alerts")({
  head: () => ({
    meta: [
      { title: "Price alerts — NexKey.gg" },
      { name: "description", content: "Your NexKey price alerts: get notified when a game drops below your target price." },
      { property: "og:title", content: "Price alerts — NexKey.gg" },
      { property: "og:description", content: "Your NexKey price alerts: get notified when a game drops below your target price." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PriceAlertsPage,
});

function PriceAlertsPage() {
  const { t, formatPrice } = useSettings();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { alerts, isLoading } = usePriceAlerts();
  const { remove } = usePriceAlertMutations();
  const [editing, setEditing] = useState<PriceAlert | null>(null);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/account/login", search: { redirect: "/account/price-alerts" }, replace: true });
  }, [loading, user, navigate]);

  if (loading || !user) {
    return <div className="container mx-auto px-4 py-16 text-sm text-muted-foreground">{t("account.loading")}</div>;
  }

  const active = alerts.filter((a) => a.active);

  return (
    <div className="container mx-auto max-w-4xl px-4 py-12">
      <Link to="/account" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary">
        <ArrowLeft className="h-3 w-3" /> {t("account.back")}
      </Link>
      <h1 className="mt-3 flex items-center gap-2 font-display text-2xl font-bold">
        <Bell className="h-5 w-5 text-primary" /> {t("alerts.title")}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("alerts.subtitle")} · {t("alerts.count").replace("{count}", String(active.length))}
      </p>

      {isLoading ? (
        <div className="mt-10 text-sm text-muted-foreground">{t("account.loading")}</div>
      ) : active.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-border bg-card p-10 text-center">
          <Bell className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">{t("alerts.empty")}</p>
          <Link
            to="/games"
            className="mt-5 inline-flex rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110"
          >
            {t("alerts.empty.cta")}
          </Link>
        </div>
      ) : (
        <ul className="mt-8 space-y-3">
          {active.map((a) => {
            const diff = a.difference;
            return (
              <li
                key={a.id}
                className="grid grid-cols-[96px_1fr] gap-4 rounded-2xl border border-border bg-card p-3 sm:grid-cols-[140px_1fr_auto] sm:items-center sm:p-4"
              >
                <Link to="/game/$slug" params={{ slug: a.game.slug }} className="block overflow-hidden rounded-lg bg-muted">
                  <div className="aspect-[460/215]">
                    {a.game.headerImage ? (
                      <img src={a.game.headerImage} alt={a.game.title} loading="lazy" className="h-full w-full object-cover" />
                    ) : (
                      <div className="grid h-full place-items-center text-[10px] text-muted-foreground">No image</div>
                    )}
                  </div>
                </Link>

                <div className="min-w-0">
                  <Link to="/game/$slug" params={{ slug: a.game.slug }} className="line-clamp-1 font-semibold hover:text-primary">
                    {a.game.title}
                  </Link>
                  <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-4">
                    <div>
                      <dt className="uppercase tracking-wider text-muted-foreground">{t("alerts.current")}</dt>
                      <dd className="font-display text-sm font-bold text-foreground">
                        {a.currentPrice != null ? formatPrice(a.currentPrice, a.currentCurrency) : t("alerts.noPrice")}
                      </dd>
                    </div>
                    <div>
                      <dt className="uppercase tracking-wider text-muted-foreground">{t("alerts.target")}</dt>
                      <dd className="font-display text-sm font-bold text-primary">{formatPrice(a.targetPrice, a.currency)}</dd>
                    </div>
                    <div>
                      <dt className="uppercase tracking-wider text-muted-foreground">{t("alerts.difference")}</dt>
                      <dd className={`font-display text-sm font-bold ${diff != null && diff <= 0 ? "text-success" : "text-foreground"}`}>
                        {diff == null ? "—" : `${diff > 0 ? "+" : diff < 0 ? "−" : ""}${formatPrice(Math.abs(diff), a.currentCurrency)}`}
                      </dd>
                    </div>
                    <div>
                      <dt className="uppercase tracking-wider text-muted-foreground">{t("alerts.status")}</dt>
                      <dd>
                        <span
                          className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                            a.triggered ? "bg-success/15 text-success" : "bg-primary/10 text-primary"
                          }`}
                        >
                          {a.triggered ? <BellRing className="h-3 w-3" /> : <Bell className="h-3 w-3" />}
                          {a.triggered ? t("alerts.status.hit") : t("alerts.status.active")}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="col-span-2 flex flex-wrap gap-2 sm:col-span-1 sm:flex-col">
                  <button
                    type="button"
                    onClick={() => setEditing(a)}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:brightness-110 sm:flex-none"
                  >
                    <Pencil className="h-3.5 w-3.5" /> {t("alerts.edit")}
                  </button>
                  <Link
                    to="/game/$slug"
                    params={{ slug: a.game.slug }}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-xs font-semibold hover:border-primary/60 sm:flex-none"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> {t("alerts.openGame")}
                  </Link>
                  <button
                    type="button"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(a.id)}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-xs font-semibold text-muted-foreground hover:border-destructive/60 hover:text-destructive disabled:opacity-60 sm:flex-none"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> {t("alerts.delete")}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <PriceAlertDialog
          open={!!editing}
          onOpenChange={(o) => !o && setEditing(null)}
          gameId={editing.gameId}
          gameTitle={editing.game.title}
          currentPrice={editing.currentPrice}
          currentCurrency={editing.currentCurrency}
          alert={editing}
        />
      )}
    </div>
  );
}
